import {
  useEffect,
  useMemo,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import useTranslation from '~/hooks/useTranslation';
import roomAssignmentStyles from '~/styles/bookingFlow/room-assignment.module.css';
import useRoomAssignments from '~/hooks/bookingFlow/useRoomAssignments';
import SelectField from '~/components/Form/SelectField';
import Alert from '~/components/General/Alert';
import type { PackageInformationRequest } from '~/types/api';
import InformationCard from './InformationCard';

export type SelectedRoomTypeValue = {
  order_session_room_type_id: number;
  pax: number;
  service_code: string;
};

type Props = {
  registerFieldPath: string;
  selectedRoomTypes: SelectedRoomTypeValue[];
  className?: string;
};

export default function RoomAssignments({
  registerFieldPath,
  className,
  selectedRoomTypes,
}: Props) {
  const translate = useTranslation();
  const { initialRoomAssignments } = useRoomAssignments();
  const { setValue } = useFormContext();

  const participants = useWatch({ name: 'participants' }) as PackageInformationRequest['participants'];
  const roomAssignments = useWatch({ name: registerFieldPath }) as PackageInformationRequest['room_assignments'];

  // Create a stable serialized key to detect nested changes in roomAssignments
  // This is needed because useWatch() may return the same array reference
  // even when nested properties change, causing useMemo to miss updates
  const roomAssignmentsKey = useMemo(
    () => JSON.stringify(roomAssignments),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(roomAssignments)],
  );

  const hasMultipleRooms = useMemo(() => {
    const roomTypes = [...new Set(roomAssignments
      .map((roomAssignment) => roomAssignment.order_session_room_type_id))];

    return roomTypes.length > 1;
  }, [roomAssignments]);

  // When the selected rooms change, we reset the room assignments.
  useEffect(() => {
    // If there're no room assignments, we set the initial value.
    const initialValue = initialRoomAssignments(selectedRoomTypes, participants);

    setValue(registerFieldPath, initialValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoomTypes, registerFieldPath, setValue, participants]);

  const roomAssignmentErrors = useMemo(() => {
    const errors: string[] = [];

    // Keep track of which participants have been assigned to a room.
    const foundParticipants: number[] = [];

    roomAssignments.forEach((roomAssignment) => {
      // If the participant is already assigned to another room, we add an error.
      // The user can only be assigned to one room.
      if (
        roomAssignment.participant_index !== null
        && foundParticipants.includes(Number(roomAssignment.participant_index))
      ) {
        errors.push(translate('bookingFlow.information.room_assignment_duplicate_participant'));
      }

      if (roomAssignment.participant_index !== null) {
        foundParticipants.push(Number(roomAssignment.participant_index));
      }

      // If this room does not have a participant assigned, we add an error.
      if (roomAssignment.participant_index === null || roomAssignment.participant_index === '') {
        errors.push(translate('bookingFlow.information.room_assignment_missing_participant'));
      }
    });

    return [...new Set(errors)];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomAssignmentsKey]);

  // If there's only one room, we don't show the room assignments.
  if (!hasMultipleRooms) {
    return null;
  }

  const roomIndexes: number[] = [];

  roomAssignments.forEach((roomAssignment) => {
    if (!roomIndexes.includes(roomAssignment.order_session_room_type_id)) {
      roomIndexes.push(roomAssignment.order_session_room_type_id);
    }
  });

  return (
    <InformationCard
      title={translate('bookingFlow.information.room_assignments')}
      description={translate('bookingFlow.information.room_assignments_description')}
      className={className}
      paddingLess
    >
      <div className={roomAssignmentStyles.roomAssignmentRows}>
        {roomAssignments.map((roomAssignment, index) => {
          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`${roomAssignment.order_session_room_type_id}_${roomAssignment.participant_index}_${index}`}
              className={roomAssignmentStyles.roomAssignment}
            >
              <div className={roomAssignmentStyles.roomAssignmentName}>
                {`${translate('bookingFlow.information.room')} ${roomIndexes.indexOf(roomAssignment.order_session_room_type_id) + 1}`}
              </div>

              <div className={roomAssignmentStyles.roomAssignmentParticipants}>
                <SelectField
                  label={translate('bookingFlow.information.select_participant')}
                  visuallyHiddenLabel
                  registerField={`${registerFieldPath}.${index}.participant_index`}
                  allowEmpty
                  options={participants.map((participant) => ({
                    label: participant.first_name && participant.last_name
                      ? `${participant.first_name} ${participant.last_name}`
                      : `${translate('bookingFlow.information.participant')} ${participant.index + 1}`,
                    value: participant.index,
                  }))}
                />
              </div>
            </div>
          );
        })}
      </div>

      {roomAssignmentErrors && roomAssignmentErrors.length > 0 && (
        <Alert type="danger" icon="alert-triangle" className={roomAssignmentStyles.roomAssignmentErrors}>
          <ul>
            {roomAssignmentErrors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}
    </InformationCard>
  );
}
